import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Maximize2,
  CircleDollarSign,
  Check,
  ArrowLeft,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { extractIdFromSlug, propertyPath } from "../lib/slug";
import { TText } from "../components/TText";
import { useLanguage } from "../context/LanguageContext";
import { useCurrentProperty } from "../context/PropertyContext";
import { t } from "../lib/i18n";
import { trackEvent, trackAdsConversion } from "../lib/analytics";

const CAT_LABEL = { rural: "Rural", urban: "Urbano", industrial: "Industrial" };

export default function PropertyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { setCurrent } = useCurrentProperty();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);

  const propId = extractIdFromSlug(slug || "");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/properties/${propId}`)
      .then(({ data }) => {
        if (cancelled) return;
        setProperty(data);
        setCurrent(data);
        // Normalize URL if user landed on /propiedad/:id without slug
        const canonical = propertyPath(data);
        if (window.location.pathname !== canonical) {
          window.history.replaceState(null, "", canonical);
        }
        // GA4 event
        trackEvent("view_property", {
          property_id: data.id,
          property_title: data.title,
          property_category: data.category,
          property_location: data.location,
        });
        // Google Ads conversion — "Vista de página"
        trackAdsConversion("3tegCNKW3socEJuW2eVD", {
          value: 1.0,
          currency: "USD",
        });
        // SEO title
        document.title = `${data.title} — ImpecLand`;
      })
      .catch(() => {
        if (!cancelled) navigate("/gallery", { replace: true });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
      document.title = "ImpecLand — Land & Real Estate Investments";
    };
  }, [propId, navigate, setCurrent]);

  useEffect(() => {
    const onKey = (e) => {
      if (!property) return;
      if (e.key === "ArrowRight")
        setIdx((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft")
        setIdx((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [property]);

  if (loading) {
    return (
      <div className="pt-40 pb-32 text-center text-[#A3A9B2]" data-testid="property-page-loading">
        Cargando...
      </div>
    );
  }
  if (!property) return null;

  const images = [property.cover_image, ...(property.images || [])].filter(
    (v, i, a) => v && a.indexOf(v) === i
  );
  const catLabel =
    lang === "es" ? CAT_LABEL[property.category] : t(lang, `cat_${property.category}`);

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = {
      title: `${property.title} — ImpecLand`,
      text: `${property.title} · ${property.location}`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        trackEvent("share_property", { property_id: property.id, method: "native" });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado al portapapeles");
        trackEvent("share_property", { property_id: property.id, method: "copy" });
      }
    } catch (_) {
      /* user cancelled */
    }
  };

  return (
    <div data-testid="property-page" className="pt-32 pb-24">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
        className="max-w-[1300px] mx-auto px-6 lg:px-12"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#A3A9B2] hover:text-[#C5A059] transition-colors"
            data-testid="property-back-button"
          >
            <ArrowLeft size={16} />
            {t(lang, "back") || "Volver"}
          </button>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#A3A9B2] hover:text-[#C5A059] transition-colors"
            data-testid="property-share-button"
          >
            <Share2 size={16} />
            Compartir
          </button>
        </div>

        <div className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-4">
          {catLabel}
        </div>
        <TText
          as="h1"
          text={property.title}
          cacheKey={`prop:${property.id}:title`}
          className="font-serif-display text-4xl md:text-6xl leading-none mb-8 text-white"
        />

        {/* Hero image */}
        <div
          className="relative aspect-[16/9] bg-[#111822] mb-6 overflow-hidden"
          onContextMenu={(e) => e.preventDefault()}
        >
          <img
            src={images[idx]}
            alt={property.title}
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            className="absolute inset-0 w-full h-full object-cover select-none"
          />
          {images.length > 1 && (
            <>
              <button
                onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-[#0A0F16]/70 border border-[#C5A059]/30 hover:border-[#C5A059] text-white"
                data-testid="gallery-prev"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => setIdx((i) => (i + 1) % images.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-[#0A0F16]/70 border border-[#C5A059]/30 hover:border-[#C5A059] text-white"
                data-testid="gallery-next"
              >
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-4 right-4 text-xs tracking-[0.15em] text-white/80 bg-[#0A0F16]/70 px-3 py-1 border border-[#C5A059]/30">
                {idx + 1} / {images.length}
              </div>
            </>
          )}
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mb-12">
            {images.map((src, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`aspect-[4/3] overflow-hidden border ${
                  i === idx ? "border-[#C5A059]" : "border-transparent"
                } hover:border-[#C5A059]/50 transition`}
                data-testid={`thumb-${i}`}
              >
                <img
                  src={src}
                  alt=""
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  className="w-full h-full object-cover select-none"
                />
              </button>
            ))}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2">
            <div className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-3">
              {t(lang, "description")}
            </div>
            <TText
              as="p"
              text={property.description}
              cacheKey={`prop:${property.id}:desc`}
              className="font-sans-body text-[#A3A9B2] text-base md:text-lg leading-relaxed mb-10 whitespace-pre-line"
            />

            {property.features?.length > 0 && (
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-4">
                  {t(lang, "features")}
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
                  {property.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#A3A9B2]">
                      <span className="mt-0.5 w-6 h-6 shrink-0 border border-[#C5A059]/40 flex items-center justify-center">
                        <Check size={12} className="text-[#C5A059]" />
                      </span>
                      <TText
                        text={f}
                        cacheKey={`prop:${property.id}:feat:${i}`}
                        className="text-sm pt-0.5"
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside className="border-l border-[#C5A059]/15 md:pl-10 space-y-6">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A9B2] mb-2 flex items-center gap-2">
                <MapPin size={12} />
                {t(lang, "location")}
              </div>
              <TText
                text={property.location}
                cacheKey={`prop:${property.id}:loc`}
                className="font-serif-display text-xl text-white"
              />
            </div>
            {property.hectares && (
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A9B2] mb-2 flex items-center gap-2">
                  <Maximize2 size={12} />
                  {t(lang, "size")}
                </div>
                <div className="font-serif-display text-xl text-white">
                  {property.hectares}
                </div>
              </div>
            )}
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A9B2] mb-2 flex items-center gap-2">
                <CircleDollarSign size={12} />
                {t(lang, "price")}
              </div>
              <div className="font-serif-display text-2xl text-[#C5A059]">
                {property.price_label || "Consultar"}
              </div>
            </div>

            <Link
              to="/contact"
              onClick={() =>
                trackEvent("click_contact_cta", { property_id: property.id })
              }
              className="block text-center bg-[#C5A059] text-[#0A0F16] hover:bg-[#E5C07B] transition-colors py-4 text-sm tracking-[0.2em] uppercase font-medium"
              data-testid="property-contact-cta"
            >
              {t(lang, "contact_for_info")}
            </Link>
          </aside>
        </div>
      </motion.div>
    </div>
  );
}
